import {
  Stack,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { useState } from "react";
import dayjs from "dayjs";
import { enUS } from "@mui/x-date-pickers/locales";
import { DateOperator } from "../../../../services/api/types/users/users";

const commonLocaleText = {
  ...enUS,
  fieldMonthPlaceholder: (params: any) =>
    params.contentType === "letter" ? "MMM" : "MM",
};
interface DateFilterFieldProps {
  index: number;
  attribute?: string;
  startDate?: string;
  endDate?: string;
  date?: string;
  operator?: string;
  disabled?: boolean;
  usedAttributes?: string[];
  onAttributeChange?: (value: string) => void;
  onOperatorChange: (value: string) => void;
  onDateChange: (start?: string, end?: string, singleDate?: string) => void;
  onRemoveRow: (index: number) => void;
}

const ATTRIBUTES = [
  { value: "assignDate", label: "Assign date" },
  { value: "deadline", label: "Deadline" },
  { value: "startDate", label: "Start date" },
  { value: "completionDate", label: "Completion date" },
];

const OPERATORS = [
  { value: "eqd", label: "Equals" },
  { value: "ned", label: "Does not equal" },
  { value: "ltd", label: "Is before" },
  { value: "gtd", label: "Is after" },
  { value: "gted", label: "Is on or after" },
  { value: "lted", label: "Is on or before" },
  { value: "betweend", label: "Is between" },
];

export const DateFilterField: React.FC<DateFilterFieldProps> = ({
  index,
  operator,
  attribute,
  startDate,
  endDate,
  date,
  disabled = false,
  usedAttributes = [],
  onAttributeChange,
  onOperatorChange,
  onDateChange,
  onRemoveRow,
}) => {
  const [openStart, setOpenStart] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);
  const isBetween = operator === DateOperator.BETWEEN;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const dateSelect = (
    <Stack spacing={1} sx={{ flex: { md: 1 }, width: { xs: "calc(50% - 6px)", md: "auto" } }}>
      <Typography variant="subtitle2">Date</Typography>
      <Select
        id={`date-filter-attribute-select-${index}`}
        value={attribute || ""}
        onChange={(e) => onAttributeChange?.(e.target.value)}
        IconComponent={ExpandMoreIcon}
        displayEmpty
        disabled={disabled}
        renderValue={(selected) =>
          selected ? (
            ATTRIBUTES.find((a) => a.value === selected)?.label
          ) : (
            <Typography color="#6B7280">Select an attribute</Typography>
          )
        }
        sx={{
          borderRadius: "8px",
          height: "44px",
          "& .MuiSelect-select": { display: "flex", alignItems: "center", padding: "8.5px 14px" },
        }}
      >
        {ATTRIBUTES.map((at) => (
          <MenuItem
            key={at.value}
            value={at.value}
            disabled={disabled || (usedAttributes.includes(at.value) && at.value !== attribute)}
          >
            {at.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );

  const operatorSelect = (
    <Stack spacing={1} sx={{ flex: { md: 1 }, width: { xs: "calc(50% - 6px)", md: "auto" } }}>
      <Typography variant="subtitle2">Operator</Typography>
      <Select
        id={`date-filter-operator-select-${index}`}
        value={operator || ""}
        onChange={(e) => onOperatorChange(e.target.value)}
        IconComponent={ExpandMoreIcon}
        displayEmpty
        disabled={disabled}
        renderValue={(selected) =>
          selected ? (
            OPERATORS.find((o) => o.value === selected)?.label
          ) : (
            <Typography color="#6B7280">Select an operator</Typography>
          )
        }
        sx={{
          borderRadius: "8px",
          height: "44px",
          "& .MuiSelect-select": { display: "flex", alignItems: "center", padding: "8.5px 14px" },
        }}
      >
        {OPERATORS.map((op) => (
          <MenuItem key={op.value} value={op.value}>
            {op.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );

  const valueField = (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">Value</Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs} localeText={commonLocaleText}>
        {isBetween ? (
          <Stack flexDirection="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
            <DatePicker
              label="Start date"
              format="DD-MMM-YYYY"
              value={startDate ? dayjs(startDate) : null}
              open={openStart}
              onOpen={() => setOpenStart(true)}
              onClose={() => setOpenStart(false)}
              onChange={(date) => { const start = date ? dayjs(date).format("DD-MMM-YYYY") : ""; onDateChange(start, undefined); }}
              disabled={disabled}
              slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
              slotProps={{
                textField: {
                  fullWidth: true, size: "small", variant: "outlined",
                  InputLabelProps: { shrink: true }, placeholder: "Start date",
                  onClick: () => setOpenStart(true),
                  InputProps: { readOnly: true, sx: { "& .MuiInputAdornment-root button": { border: "none", padding: 0, background: "transparent", "&:hover": { background: "transparent" } } } },
                },
                openPickerButton: { sx: { border: "none" } },
                openPickerIcon: { sx: { width: "15px" } },
              }}
              sx={{ flex: 1, minWidth: 0, "& .MuiPickersInputBase-root": { height: 44 }, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
            />
            <Typography sx={{ flexShrink: 0 }}>—</Typography>
            <DatePicker
              label="End date"
              format="DD-MMM-YYYY"
              value={endDate ? dayjs(endDate) : null}
              open={openEnd}
              onOpen={() => setOpenEnd(true)}
              onClose={() => setOpenEnd(false)}
              onChange={(date) => { const end = date ? dayjs(date).format("DD-MMM-YYYY") : ""; onDateChange(undefined, end); }}
              disabled={disabled}
              slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
              slotProps={{
                textField: {
                  fullWidth: true, size: "small", variant: "outlined",
                  InputLabelProps: { shrink: true }, placeholder: "End date",
                  onClick: () => setOpenEnd(true),
                  InputProps: { readOnly: true, sx: { "& .MuiInputAdornment-root button": { border: "none", padding: 0, background: "transparent", "&:hover": { background: "transparent" } } } },
                },
                openPickerButton: { sx: { border: "none" } },
                openPickerIcon: { sx: { width: "15px" } },
              }}
              sx={{ flex: 1, minWidth: 0, "& .MuiPickersInputBase-root": { height: 44 }, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
            />
          </Stack>
        ) : (
          <DatePicker
            label="Select date"
            format="DD-MMM-YYYY"
            value={date ? dayjs(date) : null}
            open={openStart}
            onOpen={() => setOpenStart(true)}
            onClose={() => setOpenStart(false)}
            onChange={(date) => { const singleDate = date ? dayjs(date).format("DD-MMM-YYYY") : ""; onDateChange(undefined, undefined, singleDate); }}
            disabled={disabled || !attribute || !operator}
            slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
            slotProps={{
              textField: {
                size: "small", variant: "outlined", fullWidth: true,
                InputLabelProps: { shrink: true }, placeholder: "DD-MMM-YYYY",
                onClick: () => setOpenStart(true),
                InputProps: { readOnly: true, sx: { "& .MuiInputAdornment-root button": { border: "none", padding: 0, background: "transparent", "&:hover": { background: "transparent" } } } },
              },
              openPickerButton: { sx: { border: "none" } },
              openPickerIcon: { sx: { width: "15px" } },
            }}
            sx={{ "& .MuiPickersInputBase-root": { height: 44 }, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
          />
        )}
      </LocalizationProvider>
    </Stack>
  );

  const minusButton = (
    <IconButton
      onClick={() => onRemoveRow(index)}
      disabled={disabled}
      sx={{
        bgcolor: "#FEE4E2",
        borderColor: "#FDA29B",
        color: "#FDA29B",
        borderRadius: "12px",
        height: "44px",
        width: "44px",
        flexShrink: 0,
        "&:hover": { bgcolor: "#FEF3F2" },
      }}
    >
      <RemoveOutlinedIcon />
    </IconButton>
  );

  return (
    <Box
      sx={{
        border: { xs: "1px solid #E5E7EB", md: "none" },
        borderRadius: { xs: "12px", md: 0 },
        p: { xs: 2, md: 0 },
        backgroundColor: { xs: "#F9FAFB", md: "transparent" },
      }}
    >
      {isDesktop ? (
        /* Desktop: single row */
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <Stack flexDirection="row" alignItems="flex-start" flex={1} gap={2}>
            {dateSelect}
            {operatorSelect}
            <Stack spacing={1} sx={{ flex: isBetween ? 4 : 1, minWidth: 0 }}>
              {valueField}
            </Stack>
          </Stack>
          {minusButton}
        </Box>
      ) : (
        /* Tablet: two rows */
        <Box>
          <Stack direction="row" gap={1.5} alignItems="flex-end">
            {dateSelect}
            {operatorSelect}
          </Stack>
          <Stack direction="row" gap={1} alignItems="flex-end" mt={1.5}>
            {valueField}
            {minusButton}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
